import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  password: string;
}

/**
 * Password strength meter component
 * Provides visual feedback about password strength
 */
function PasswordStrengthMeter({ password }: Props) {
  const { t } = useTranslation();
  
  // Calculate password strength
  const calculateStrength = (password: string): number => {
    let strength = 0;
    
    // Basic length check - minimum 8 characters
    if (password.length >= 8) {
      strength += 1;
    }
    
    // Check for numbers
    if (/\d/.test(password)) {
      strength += 1;
    }
    
    // Check for lowercase letters
    if (/[a-z]/.test(password)) {
      strength += 1;
    }
    
    // Check for uppercase letters
    if (/[A-Z]/.test(password)) {
      strength += 1;
    }
    
    // Check for special characters
    if (/[^A-Za-z0-9]/.test(password)) {
      strength += 1;
    }
    
    return strength;
  };
  
  const strength = calculateStrength(password);
  
  // Determine label and color based on strength
  const getLabel = (): string => {
    switch (strength) {
      case 0:
      case 1:
        return t('auth.passwordStrengthWeak', 'Weak');
      case 2:
      case 3:
        return t('auth.passwordStrengthMedium', 'Medium');
      case 4:
        return t('auth.passwordStrengthStrong', 'Strong');
      case 5:
        return t('auth.passwordStrengthVeryStrong', 'Very Strong');
      default:
        return '';
    }
  };
  
  const getColorClass = (): string => {
    switch (strength) {
      case 0:
      case 1:
        return 'bg-danger';
      case 2:
        return 'bg-warning';
      case 3:
        return 'bg-info';
      case 4:
        return 'bg-primary';
      case 5:
        return 'bg-success';
      default:
        return 'bg-secondary';
    }
  };
  
  // Calculate width percentage based on strength (20% per level)
  const getWidthPercentage = (): string => {
    return `${(strength / 5) * 100}%`;
  };
  
  return (
    <div className="mt-2">
      <div className="d-flex justify-content-between mb-1">
        <small className="text-muted">{t('auth.passwordStrength', 'Password Strength')}:</small>
        <small className={`text-${getColorClass().replace('bg-', '')}`}>{getLabel()}</small>
      </div>
      <div className="progress" style={{ height: '5px' }}>
        <div 
          className={`progress-bar ${getColorClass()}`} 
          role="progressbar" 
          style={{ width: getWidthPercentage() }} 
          aria-valuenow={strength} 
          aria-valuemin={0} 
          aria-valuemax={5}
        ></div>
      </div>
      {strength <= 2 && (
        <small className="d-block mt-1 text-muted">
          {t('auth.passwordTip', 'Tip: Use a mix of letters, numbers, and symbols for a stronger password')}
        </small>
      )}
    </div>
  );
}

export default PasswordStrengthMeter; 